import { useParams } from 'react-router-dom'

import { PageHeader } from '@/app/layouts/PageHeader'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useList, useListMembers, useListMembership } from '@/features/lists'
import { UserListItem } from '@/features/profile'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useT } from '@/i18n'

export function ListMembersPage() {
  const t = useT()
  const { listId = '' } = useParams()
  const list = useList(listId)
  const query = useListMembers(listId)
  const membership = useListMembership()

  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  })

  const users = query.data?.pages.flatMap((page) => page.items) ?? []
  const isOwner = Boolean(list.data?.viewer.is_owner)

  return (
    <>
      <PageHeader title={t('lists.members')} subtitle={list.data?.name} back />

      {query.isPending && <PostListSkeleton count={4} />}
      {query.isError && (
        <ErrorState message={query.error.message} onRetry={() => query.refetch()} />
      )}
      {!query.isPending && !query.isError && users.length === 0 && (
        <EmptyState
          title={t('lists.noMembers')}
          description={isOwner ? t('lists.timelineEmptyOwner') : undefined}
        />
      )}

      {users.map((user) => (
        <UserListItem
          key={user.id}
          user={user}
          actions={
            isOwner ? (
              <Button
                variant="outline"
                size="sm"
                loading={membership.isPending && membership.variables?.username === user.username}
                onClick={(event) => {
                  // 行本身是去主页的链接
                  event.preventDefault()
                  membership.mutate({ listId, username: user.username, member: true })
                }}
              >
                {t('lists.removeMember')}
              </Button>
            ) : undefined
          }
        />
      ))}

      <div ref={sentinelRef} aria-hidden className="h-px" />
      {query.isFetchingNextPage && <CenteredSpinner />}
    </>
  )
}
